import { Component, OnInit, ViewChild } from '@angular/core';
import { PartConfigModelSize, PartType } from 'app/core/model/entity-model';
import { DxDataGridComponent } from 'devextreme-angular';
import { alert, confirm } from 'devextreme/ui/dialog';
import _ from 'lodash';
import { BsModalService } from 'ngx-bootstrap/modal';
import { AppInformationService, GenesisUnitOfWork } from '../../core/core.module';
import { InputDialogComponent } from '../../shared/input-dialog/input-dialog.component';
import { ModelSizeDialogComponent } from '../dialogs/model-size-dialog.component';
import { ModelTypeDialogComponent } from '../dialogs/model-type-dialog.component';
import { NameValueDialogComponent } from '../dialogs/name-value-dialog.component';
import { SuspensionDialogComponent } from '../dialogs/suspension-dialog.component';
import { WinchStandDialogComponent } from '../dialogs/winch-stand-dialog.component';
import { TireDialogComponent } from '../dialogs/tire-dialog.component';
import { BrakeDialogComponent } from '../dialogs/brake-dialog.component';

@Component({
    selector: 'app-part-configurator',
    templateUrl: './part-configurator.component.html',
    styleUrls: ['./part-configurator.component.scss'],
    standalone: false,
})
export class PartConfiguratorComponent implements OnInit {
    gridColumns: any[] = [];
    entities;
    // entityTypes = ['Axles', 'Brakes', 'Options', 'Suspensions', 'Tires', 'Winch Stands', 'Model Types', 'Model Sizes'];
    entityTypes = [
        { name: 'Model Types', entityType: 'partConfigModelTypes' },
        { name: 'Model Sizes', entityType: 'partConfigModelSizes' },
        { name: 'Axles', entityType: 'partConfigAxles', dataLocked: true },
        { name: 'Brakes', entityType: 'partConfigBrakes', dataLocked: false },
        { name: 'Config Options', entityType: 'partConfigOptions' },
        { name: 'Cross Arms', entityType: 'partConfigCrossArms' },
        { name: 'Place Quantities', entityType: 'partConfigPlaceQuantities' },
        { name: 'Suspensions', entityType: 'partConfigSuspensions' },
        { name: 'Tires', entityType: 'partConfigTires' },
        { name: 'Winch Stands', entityType: 'partConfigWinchStands' },
        { name: 'Winch Stand Options', entityType: 'partConfigWinchStandOptions' },
        { name: 'General', entityType: 'partConfigGeneralSettings' },
    ];
    entityType: any;

    // DEFAULTS : OVERRIDE AS NEEDED
    orderProperty = 'displayOrder';
    valueProperty = 'name';
    valuePrompt = 'Name';
    allowReorder = true;
    allowDelete = true;

    @ViewChild('dataGrid') dataGrid: DxDataGridComponent;

    constructor(
        private uow: GenesisUnitOfWork,
        private appInfo: AppInformationService,
        private modalService: BsModalService,
    ) {}

    async ngOnInit() {
        if (this.entityTypes.length > 0) this.entityType = this.entityTypes[1];
        this.entityTypeValueChanged(null);
    }

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarButtons',
            },
            {
                template: 'toolbarText',
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'revert',
                    elementAttr: { title: 'Reset Filters' },
                    onClick: () => {
                        e.component.clearFilter();
                    },
                },
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'refresh',
                    elementAttr: { title: 'Reset Grid Layout' },
                    onClick: () => {
                        e.component.state({});
                    },
                },
                location: 'after',
            },
        );
    }

    hasChanges() {
        return this.uow.hasChanges();
    }

    async save() {
        await this.uow.commit();
    }
    cancel() {
        this.uow.rollback();
    }

    async entityTypeValueChanged(e) {
        this.setupForm();
        await this.getData();
    }

    setupForm() {
        switch (this.entityType.name) {
            case 'Axles':
                this.gridColumns = [{ dataField: 'name', width: 200 }, { dataField: 'axleCount', width: 100 }, { width: 100 }];
                break;

            case 'Brakes':
                this.gridColumns = [
                    { dataField: 'name', width: 200 },
                    { dataField: 'value', caption: 'Value', width: 125 },
                    { dataField: 'disableTireSelection', width: 200 },
                    { dataField: 'isTorsionSuspension', width: 200 },
                    { dataField: 'basePrice', width: 200 },
                    { dataField: 'price2Axle', width: 200 },
                    { dataField: 'price3Axle', width: 200 },
                    { width: 100 },
                ];
                break;

            case 'Cross Arms':
            case 'Place Quantities':
            case 'Config Options':
            case 'Winch Stand Options':
            case 'General':
                this.gridColumns = [{ dataField: 'name', width: 125 }, { dataField: 'value', width: 125 }, { width: 100 }];
                break;

            case 'Tires':
                this.gridColumns = [
                    { dataField: 'name', width: 125 },
                    { dataField: 'value', width: 125 },
                    { dataField: 'spareTire.partNumber', caption: 'Spare Tire Part#', width: 125 },
                    { dataField: 'upgradePrice', width: 125, format: '$#,##0.00', dataType: 'number' },
                    { dataField: 'singleFenderPrice', width: 125, format: '$#,##0.00', dataType: 'number' },
                    { dataField: 'tandemFenderPrice', width: 125, format: '$#,##0.00', dataType: 'number' },
                    { dataField: 'tripleFenderPrice', width: 125, format: '$#,##0.00', dataType: 'number' },
                    { width: 100 },
                ];
                break;

            case 'Suspensions':
                this.gridColumns = [
                    { dataField: 'name', width: 125 },
                    { dataField: 'value', caption: 'Part# Value', alignment: 'left', width: 125 },
                    { dataField: 'tiresHtml', caption: 'Tires', width: 250, encodeHtml: false },
                    { width: 100 },
                ];
                break;

            case 'Model Types':
                this.gridColumns = [
                    { dataField: 'name', caption: 'Name', width: 200 },
                    { dataField: 'description', width: 250 },
                    { dataField: 'hasAxles', caption: 'Has Axles Qty Options', width: 100 },
                    { dataField: 'hasBrakes', caption: 'Has Brake Options', width: 100 },
                    { dataField: 'hasTailOption', caption: 'Has Tail Option', width: 100 },
                    { dataField: 'hasPolyCarpetOption', caption: 'Has Poly/Carpet Options', width: 100 },
                    { dataField: 'hasSuspension', caption: 'Has Suspension Options', width: 100 },
                    { dataField: 'hasWinchStand', caption: 'Has Winch Stand Options', width: 100 },
                    { dataField: 'hasOptions', caption: 'Has Config Options', width: 100 },
                    { dataField: 'hasTreeOptions', caption: 'Has Tree Options', width: 100 },
                    { dataField: 'hasToonOption', caption: 'Has Toon Option', width: 100 },
                    { width: 100 },
                ];
                this.allowReorder = true;
                break;

            case 'Model Sizes':
                this.gridColumns = [
                    { dataField: 'partConfigModelType.name', caption: 'Model Type', width: 100 },
                    { dataField: 'name', caption: 'Name', width: 100 },
                    { dataField: 'description', width: 200 },
                    { dataField: 'noBrakeOption', width: 100 },
                    { dataField: 'noTailOption', width: 100 },
                    { dataField: 'axleHtml', caption: 'Axles', width: 100, encodeHtml: false },
                    { dataField: 'brakeHtml', caption: 'Brakes', width: 100, encodeHtml: false },
                    { dataField: 'tireHtml', caption: 'Tires', width: 100, encodeHtml: false },
                    { dataField: 'suspensionHtml', caption: 'Suspensions', width: 100, encodeHtml: false },
                    { dataField: 'optionHtml', caption: 'Options', width: 150, encodeHtml: false },
                    { dataField: 'winchStandHtml', caption: 'Winch Stands', width: 100, encodeHtml: false },
                    { dataField: 'placeQtyHtml', caption: 'Place Qty Options', width: 100, encodeHtml: false },
                ];
                this.allowReorder = false;
                break;

            case 'Winch Stands':
                this.gridColumns = [
                    { dataField: 'name', width: 125 },
                    { dataField: 'value', width: 125 },
                    { dataField: 'optionsHtml', caption: 'Options', width: 250, encodeHtml: false },
                    { width: 100 },
                ];
                break;

            default: {
                this.gridColumns = [];
                break;
            }
        }
    }

    async getData() {
        switch (this.entityType.name) {
            case 'Model Types':
            case 'Axles':
            case 'Brakes':
            case 'Cross Arms':
            case 'Place Quantities':
            case 'Config Options':
            case 'Winch Stand Options': 
            case 'General': {
                this.entities = await this.uow[this.entityType.entityType].whereActive(this.orderProperty);
                break;
            }

            case 'Tires': {
                this.entities = await this.uow[this.entityType.entityType].whereActive(this.orderProperty, 'spareTire');
                break;
            }

            case 'Suspensions': {
                const entities = await this.uow[this.entityType.entityType].whereActive(this.orderProperty, 'partConfigSuspensionTireLinks.partConfigTire');
                entities.forEach((entity) => {
                    entity.setSuspensionTireLinks(entity.partConfigSuspensionTireLinks, []);
                });
                this.entities = entities;
                console.log('suspensions entities', this.entities);
                break;
            }

            case 'Model Sizes': {
                const entities = (await this.uow.partConfigModelSizes.whereActive('partConfigModelTypeId', 'partConfigModelType, partConfigSizeLinks')) as PartConfigModelSize[];
                const axles = await this.uow.partConfigAxles.whereActive('id');
                const tires = await this.uow.partConfigTires.whereActive('id');
                const suspensions = await this.uow.partConfigSuspensions.whereActive('id');
                const options = await this.uow.partConfigOptions.whereActive('id');
                const winchStands = await this.uow.partConfigWinchStands.whereActive('id', 'partConfigWinchStandOptionLinks.partConfigWinchStandOption');
                const placeQtys = await this.uow.partConfigPlaceQuantities.whereActive('id');
                const brakes = await this.uow.partConfigBrakes.whereActive('id');
                winchStands.forEach((entity) => {
                    entity.setOptionLinks(entity.partConfigWinchStandOptionLinks);
                });

                entities.forEach((entity) => {
                    entity.setSizeLinks(entity.partConfigSizeLinks, axles, tires, suspensions, options, winchStands, placeQtys, brakes);
                });
                this.entities = entities;
                break;
            }

            case 'Winch Stands': {
                const entities = await this.uow[this.entityType.entityType].whereActive(this.orderProperty, 'partConfigWinchStandOptionLinks.partConfigWinchStandOption');
                entities.forEach((entity) => {
                    entity.setOptionLinks(entity.partConfigWinchStandOptionLinks);
                });
                this.entities = entities;
                break;
            }
        }
    }

    getMaxDisplayOrder() {
        if (!this.entities || this.entities.length === 0) return 1;
        const maxEntity = _.maxBy(this.entities, this.orderProperty);
        return (maxEntity ? (maxEntity[this.orderProperty] ?? 0) : 0) + 1;
    }

    async open(entity?: any) {
        switch (this.entityType.name) {
            case 'Config Options':
            case 'Winch Stand Options':
            case 'Cross Arms':
            case 'General':
            case 'Place Quantities': {
                await this.openNameValueDialog(entity, this.entityType.entityType);
                break;
            }
            case 'Brakes': {
                await this.openBrakeDialog(entity, this.entityType.entityType);
                break;
            }
            case 'Tires': {
                await this.openTireDialog(entity, this.entityType.entityType);
                break;
            }
            case 'Model Types': {
                await this.openModelType(entity);
                break;
            }
            case 'Model Sizes': {
                await this.openModelSize(entity);
                break;
            }
            case 'Suspensions': {
                await this.openModelSuspension(entity);
                break;
            }
            case 'Winch Stands': {
                await this.openWinchStand(entity);
                break;
            }

            default: {
                break;
            }
        }
    }

    setProperty(entity) {
        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: this.entityType,
                dialogPrompt: `Enter ${this.valuePrompt}`,
                dialogValue: entity[this.valueProperty],
                mode: 'text',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.result && !!result.value) {
                entity[this.valueProperty] = result.value;
                await this.uow.commit();
                this.getData();
            } else {
                this.uow.rollback();
            }
            modalRef.hide();
        });
    }

    async openNameValueDialog(entity?: any, entityType?: string) {
        const modalRef = this.modalService.show(NameValueDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                entity: entity,
                entityType: entityType,
                displayOrder: this.getMaxDisplayOrder(),
            },
        });
        modalRef.content.result.subscribe(async (result) => {
            console.log('result', result);
            if (result.success && !!result.value) {
                await this.uow.commit();
                await this.getData();
            } else {
                this.uow.rollback();
            }
            modalRef.hide();
        });
    }

    async openBrakeDialog(entity?: any, entityType?: string) {
        const modalRef = this.modalService.show(BrakeDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                entity: entity,
                entityType: entityType,
            },
        });
        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                await this.uow.commit();
                await this.getData();
            }
            modalRef.hide();
            this.appInfo.pleaseWaitHide();
        });
    }

    async openTireDialog(entity?: any, entityType?: string) {
        const modalRef = this.modalService.show(TireDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                entity: entity,
                entityType: entityType,
            },
        });
        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                await this.uow.commit();
                await this.getData();
            }
            modalRef.hide();
            this.appInfo.pleaseWaitHide();
        });
    }

    async openModelType(entity?: any) {
        const modalRef = this.modalService.show(ModelTypeDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                modelType: entity,
                displayOrder: this.getMaxDisplayOrder(),
            },
        });
        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                await this.uow.commit();
                await this.getData();
            }
            modalRef.hide();
            this.appInfo.pleaseWaitHide();
        });
    }
    async openModelSize(entity?: any) {
        const modalRef = this.modalService.show(ModelSizeDialogComponent, {
            class: entity ? 'modal-lg' : 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                modelSize: entity,
                isNew: !entity,
                displayOrder: this.getMaxDisplayOrder(),
            },
        });
        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                await this.uow.commit();
                await this.getData();
            }
            modalRef.hide();
            this.appInfo.pleaseWaitHide();
        });
    }
    async openModelSuspension(entity?: any) {
        const modalRef = this.modalService.show(SuspensionDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                suspension: entity,
                displayOrder: this.getMaxDisplayOrder(),
            },
        });
        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                await this.uow.commit();
                await this.getData();
            }
            modalRef.hide();
            this.appInfo.pleaseWaitHide();
        });
    }

    async openWinchStand(entity?: any) {
        const modalRef = this.modalService.show(WinchStandDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                winchStand: entity,
                displayOrder: this.getMaxDisplayOrder(),
            },
        });
        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                await this.uow.commit();
            }
            await this.getData();
            modalRef.hide();
            this.appInfo.pleaseWaitHide();
        });
    }

    async delete(entity) {
        if (!entity) return;
        if (this.entityType === 'Part Type') {
            // this same approach for any entity type that has deleted field.
            if (entity.id === PartType.Types.Finished || entity.id === PartType.Types.Trailer || entity.id === PartType.Types.Replacement) {
                await alert(
                    `PartTypes 'Finished', 'Trailer', and 'Replacement' cannot be deleted because they are used in the code other places.`,
                    `${this.appInfo.settings.company}`,
                );
                return;
            }
        }

        const result = await (confirm(`Are you sure you want to DELETE this item?`, `${this.appInfo.settings.company}`) as Promise<boolean>);
        if (!!result) {
            entity.deleted = new Date();
            await this.uow.commit();
            await this.getData();
        }
    }

    async rowDoubleClick(e) {
        if (e.rowType === 'data') {
            await this.open(e.data);
        }
    }

    onReorder = async (e) => {
        const visibleRows = e.component.getVisibleRows(),
            toIndex = this.entities.indexOf(visibleRows[e.toIndex].data),
            fromIndex = this.entities.indexOf(e.itemData);

        this.entities.splice(fromIndex, 1);
        this.entities.splice(toIndex, 0, e.itemData);

        for (let idx = 0; idx < this.entities.length; idx++) {
            this.entities[idx][this.orderProperty] = idx + 1;
        }
        await this.uow.commit();
    };
}
